import { useEffect, useState } from "react";
import {
  Modal,
  Spin,
  Tag,
  Empty,
  Table,
  Space,
  Typography,
  Tabs,
  Tooltip,
  Card,
  Row,
  Col,
  Statistic,
  Divider,
} from "antd";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  ExclamationCircleOutlined,
  InfoCircleOutlined,
  EnvironmentOutlined,
  ExperimentOutlined,
  CalendarOutlined,
  AreaChartOutlined,
  AuditOutlined,
  TagsOutlined,
  HomeOutlined,
  AppstoreOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";

import { apiClient } from "../../../services/api/apiClient";

/* ------------------------------------------------------------------ */
/* Tipos                                                              */
/* ------------------------------------------------------------------ */

interface Variety {
  id: number | null;
  code: string | null;
  name: string;
}

interface AuditLink {
  id: number;
  reference: string | null;
  start_date: string | null;
  audit_type_name: string | null;
}

interface Snapshot {
  id: number;
  season_id: number | null;
  season_year: number | null;
  season_name: string | null;
  status: string;
  verified_surface: number | null;
  verified_plants: number | null;
  verified_variety_id: number | null;
  verified_variety_name: string | null;
  verified_crop_date: number | null;
  verified_kg_plot: number | null;
  production_blocks: Array<{
    percentage: number;
    avg_yield: number;
    avg_caliber: number;
  }>;
  verified_by_name: string | null;
  verified_at: string | null;
  validated_by_name: string | null;
  validated_at: string | null;
}

interface Revision {
  id: string;
  created: string;
  change_summary: string;
  changed_by_name: string | null;
  audit_id: number | null;
  audit_reference: string | null;
  audit_start_date: string | null;
  changed_fields: Record<string, any>;
  variety_id: number | null;
  variety_name: string | null;
}

export interface PlotRealDetail {
  id: number;
  name: string | null;
  farm: {
    id: number;
    name: string | null;
    producer_id: number | null;
    producer_name: string | null;
    province_name: string | null;
    town_name: string | null;
  } | null;
  plot_type_id: number | null;
  plot_type_name: string | null;
  variety: Variety | null;
  rootstock_id: number | null;
  rootstock_name: string | null;
  intermediate_wood_id: number | null;
  intermediate_wood_name: string | null;
  crop_date: number | null;
  plantation_date: string | null;
  plantation_frame_row: number | null;
  plantation_frame_column: number | null;
  plantation_frame: string | null;
  plants: number | null;
  grafting_type: number | null;
  grafting_type_label: string | null;
  grafting: number | null;
  surface: number | null;
  surface_gps: number | null;
  season_id: number | null;
  season_name: string | null;
  is_active: boolean;
  is_illegal: boolean;
  status: "open" | "closed";
  closed_date: string | null;
  comment_closed: string | null;
  created_in_audit: AuditLink | null;
  closed_in_audit: AuditLink | null;
  audit_reference_legacy: number | null;
  has_geom: boolean;
  snapshots: Snapshot[];
  revisions: Revision[];
  created: string | null;
  modified: string | null;
}

interface Props {
  plotRealId: number | null;
  open: boolean;
  onClose: () => void;
  onNavigateToAudit?: (auditId: number) => void;
}

/* ------------------------------------------------------------------ */
/* Helpers                                                            */
/* ------------------------------------------------------------------ */

const fmtNum = (v: number | null | undefined, dec = 2) =>
  v != null
    ? Number(v).toLocaleString("es-ES", {
        minimumFractionDigits: dec,
        maximumFractionDigits: dec,
      })
    : "—";

const fmtKg = (v: number | null | undefined) =>
  v != null
    ? Number(v).toLocaleString("es-ES", { maximumFractionDigits: 0 })
    : "—";

const fmtDate = (v: string | null | undefined) =>
  v ? dayjs(v).format("DD/MM/YYYY") : "—";

const fmtDateTime = (v: string | null | undefined) =>
  v ? dayjs(v).format("DD/MM/YYYY HH:mm") : "—";

/* ------------------------------------------------------------------ */
/* Componente                                                         */
/* ------------------------------------------------------------------ */

export const RealPlotDetailModal = ({
  plotRealId,
  open,
  onClose,
  onNavigateToAudit,
}: Props) => {
  const [data, setData] = useState<PlotRealDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || plotRealId == null) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const r = await apiClient.get<PlotRealDetail>(
          `/plot-reals/${plotRealId}/detail/`,
        );
        if (!cancelled) setData(r.data);
      } catch (e: any) {
        if (!cancelled) {
          setError(
            e?.response?.data?.detail ??
              e?.message ??
              "No se pudo cargar el detalle de la parcela.",
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, plotRealId]);

  /* ---- Render ----------------------------------------------------- */

  const renderGeneral = () => {
    if (!data) return null;

    const fieldRow = (
      label: string,
      value: React.ReactNode,
      hint?: string,
    ) => (
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "6px 0",
          borderBottom: "1px dashed #f0f0f0",
        }}
      >
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {label}
          {hint && (
            <Tooltip title={hint}>
              <InfoCircleOutlined style={{ marginLeft: 4, color: "#bfbfbf" }} />
            </Tooltip>
          )}
        </Typography.Text>
        <span style={{ fontSize: 13, textAlign: "right" }}>{value}</span>
      </div>
    );

    const cardHead = (icon: React.ReactNode, title: string) => (
      <Space size={6}>
        {icon}
        <span>{title}</span>
      </Space>
    );

    return (
      <>
        {/* Hero header */}
        <Card
          size="small"
          style={{ marginBottom: 12, background: "#fafafa" }}
          bodyStyle={{ padding: "12px 16px" }}
        >
          <Row gutter={16} align="middle">
            <Col flex="auto">
              <Space direction="vertical" size={2}>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  <HomeOutlined /> {data.farm?.name || "Sin finca"}
                  {data.farm?.producer_name && (
                    <>
                      {" · "}
                      <Typography.Text strong style={{ fontSize: 12 }}>
                        {data.farm.producer_name}
                      </Typography.Text>
                    </>
                  )}
                </Typography.Text>
                {(data.farm?.town_name || data.farm?.province_name) && (
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                    <EnvironmentOutlined />{" "}
                    {[data.farm.town_name, data.farm.province_name]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography.Text>
                )}
                <Typography.Title level={3} style={{ margin: 0 }}>
                  {data.name || "—"}
                </Typography.Title>
              </Space>
            </Col>
            <Col>
              <Space size={4} wrap>
                {data.is_active ? (
                  <Tag color="green" icon={<CheckCircleOutlined />}>
                    Abierta
                  </Tag>
                ) : (
                  <Tag color="default" icon={<CloseCircleOutlined />}>
                    Cerrada
                  </Tag>
                )}
                {data.is_illegal && (
                  <Tag color="red" icon={<ExclamationCircleOutlined />}>
                    Ilegal
                  </Tag>
                )}
                {data.has_geom && (
                  <Tooltip title="Tiene geometría dibujada">
                    <Tag color="geekblue" icon={<EnvironmentOutlined />}>
                      Geo
                    </Tag>
                  </Tooltip>
                )}
                {data.variety && (
                  <Tag color="blue" icon={<TagsOutlined />}>
                    {data.variety.name}
                  </Tag>
                )}
              </Space>
            </Col>
          </Row>
        </Card>

        {/* KPIs destacados */}
        <Row gutter={12} style={{ marginBottom: 12 }}>
          <Col xs={12} sm={6}>
            <Card size="small" bodyStyle={{ padding: "12px 16px" }}>
              <Statistic
                title="Superficie auditada"
                value={data.surface ?? 0}
                precision={2}
                suffix="ha"
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" bodyStyle={{ padding: "12px 16px" }}>
              <Statistic
                title="Superficie GPS"
                value={data.surface_gps ?? 0}
                precision={2}
                suffix="ha"
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" bodyStyle={{ padding: "12px 16px" }}>
              <Statistic
                title="Plantas"
                value={data.plants ?? 0}
                groupSeparator="."
                valueStyle={{ fontSize: 18 }}
              />
            </Card>
          </Col>
          <Col xs={12} sm={6}>
            <Card size="small" bodyStyle={{ padding: "12px 16px" }}>
              <Statistic
                title="Año plantación"
                value={data.crop_date ?? "—"}
                valueStyle={{ fontSize: 18 }}
                groupSeparator=""
              />
            </Card>
          </Col>
        </Row>

        {/* Detalle agrupado */}
        <Row gutter={12}>
          <Col xs={24} md={12}>
            <Card
              size="small"
              title={cardHead(<ExperimentOutlined />, "Identificación")}
              style={{ marginBottom: 12 }}
              bodyStyle={{ padding: "8px 16px" }}
            >
              {fieldRow(
                "Tipo de plantación",
                data.plot_type_name ? (
                  <Tag>{data.plot_type_name}</Tag>
                ) : (
                  <Tag color="warning">Sin asignar</Tag>
                ),
              )}
              {fieldRow(
                "Variedad protegida",
                data.variety ? (
                  <Tag color="blue">{data.variety.name}</Tag>
                ) : (
                  "—"
                ),
              )}
              {fieldRow(
                "Patrón",
                data.rootstock_name ? (
                  <Tag color="purple">{data.rootstock_name}</Tag>
                ) : (
                  "—"
                ),
                "Portainjerto",
              )}
              {fieldRow(
                "Madera intermedia",
                data.intermediate_wood_name || "—",
              )}
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card
              size="small"
              title={cardHead(<CalendarOutlined />, "Plantación")}
              style={{ marginBottom: 12 }}
              bodyStyle={{ padding: "8px 16px" }}
            >
              {fieldRow(
                "Fecha de plantación",
                fmtDate(data.plantation_date),
              )}
              {fieldRow(
                "Marco",
                data.plantation_frame ? (
                  <Tag>{data.plantation_frame}</Tag>
                ) : (
                  "—"
                ),
              )}
              {fieldRow(
                "Tipo de injerto",
                data.grafting_type_label ? (
                  <Tag color="cyan">{data.grafting_type_label}</Tag>
                ) : (
                  data.grafting_type ?? "—"
                ),
              )}
              {fieldRow(
                "Nº de injertos",
                data.grafting != null
                  ? data.grafting.toLocaleString("es-ES")
                  : "—",
              )}
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card
              size="small"
              title={cardHead(<AreaChartOutlined />, "Superficies y campaña")}
              style={{ marginBottom: 12 }}
              bodyStyle={{ padding: "8px 16px" }}
            >
              {fieldRow(
                "Sup. auditada",
                <strong>{fmtNum(data.surface)} ha</strong>,
              )}
              {fieldRow(
                "Sup. GPS",
                <strong>{fmtNum(data.surface_gps)} ha</strong>,
                "Calculada a partir del polígono dibujado",
              )}
              {fieldRow(
                "Campaña asignada",
                data.season_name ? (
                  <Tag color="gold">{data.season_name}</Tag>
                ) : (
                  "—"
                ),
              )}
              {fieldRow(
                "Finca",
                data.farm?.name ? (
                  <Space size={4} wrap style={{ justifyContent: "flex-end" }}>
                    <span>{data.farm.name}</span>
                    {data.farm.producer_name && (
                      <Tag color="geekblue">{data.farm.producer_name}</Tag>
                    )}
                  </Space>
                ) : (
                  "—"
                ),
              )}
              {(data.farm?.town_name || data.farm?.province_name) &&
                fieldRow(
                  "Ubicación",
                  <Space size={4} wrap style={{ justifyContent: "flex-end" }}>
                    {data.farm?.town_name && (
                      <Tag>{data.farm.town_name}</Tag>
                    )}
                    {data.farm?.province_name && (
                      <Tag color="default">{data.farm.province_name}</Tag>
                    )}
                  </Space>,
                )}
            </Card>
          </Col>

          <Col xs={24} md={12}>
            <Card
              size="small"
              title={cardHead(<AuditOutlined />, "Auditorías vinculadas")}
              style={{ marginBottom: 12 }}
              bodyStyle={{ padding: "8px 16px" }}
            >
              {fieldRow(
                "Origen",
                data.created_in_audit ? (
                  <a
                    onClick={() =>
                      onNavigateToAudit?.(data.created_in_audit!.id)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    #{data.created_in_audit.id}
                    {data.created_in_audit.start_date &&
                      ` · ${fmtDate(data.created_in_audit.start_date)}`}
                  </a>
                ) : (
                  "—"
                ),
                data.created_in_audit?.audit_type_name ?? undefined,
              )}
              {fieldRow(
                "Cierre",
                data.closed_in_audit ? (
                  <a
                    onClick={() =>
                      onNavigateToAudit?.(data.closed_in_audit!.id)
                    }
                    style={{ cursor: "pointer" }}
                  >
                    #{data.closed_in_audit.id}
                    {data.closed_in_audit.start_date &&
                      ` · ${fmtDate(data.closed_in_audit.start_date)}`}
                  </a>
                ) : (
                  "—"
                ),
              )}
              {data.closed_date &&
                fieldRow("Fecha de cierre", fmtDate(data.closed_date))}
              {data.comment_closed && (
                <>
                  <Divider style={{ margin: "8px 0" }} />
                  <Typography.Text type="secondary" italic>
                    "{data.comment_closed}"
                  </Typography.Text>
                </>
              )}
            </Card>
          </Col>
        </Row>

        {/* Footer técnico */}
        <Card
          size="small"
          title={cardHead(<AppstoreOutlined />, "Metadatos")}
          bodyStyle={{ padding: "8px 16px" }}
        >
          <Row gutter={16}>
            <Col xs={12} md={6}>
              {fieldRow("Creada", fmtDateTime(data.created))}
            </Col>
            <Col xs={12} md={6}>
              {fieldRow("Modificada", fmtDateTime(data.modified))}
            </Col>
            <Col xs={12} md={6}>
              {fieldRow("ID interno", `#${data.id}`)}
            </Col>
            <Col xs={12} md={6}>
              {fieldRow(
                "Status",
                <Tag color={data.status === "closed" ? "default" : "green"}>
                  {data.status}
                </Tag>,
              )}
            </Col>
          </Row>
        </Card>
      </>
    );
  };

  const renderSnapshots = () => {
    if (!data) return null;
    if (data.snapshots.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin snapshots de campaña"
        />
      );
    }
    return (
      <Table
        dataSource={data.snapshots}
        rowKey="id"
        size="small"
        pagination={false}
      >
        <Table.Column
          title="Campaña"
          dataIndex="season_name"
          width={220}
          render={(v: string | null, r: Snapshot) =>
            v || (r.season_year != null ? String(r.season_year) : "—")
          }
        />
        <Table.Column
          title="Estado"
          dataIndex="status"
          width={120}
          render={(s: string) => <Tag>{s || "—"}</Tag>}
        />
        <Table.Column
          title="Variedad verificada"
          dataIndex="verified_variety_name"
          width={180}
          render={(v: string | null) =>
            v ? <Tag color="blue">{v}</Tag> : "—"
          }
        />
        <Table.Column
          title="Sup. ver. (ha)"
          dataIndex="verified_surface"
          width={120}
          align="right"
          render={(v: number | null) => fmtNum(v)}
        />
        <Table.Column
          title="Plantas ver."
          dataIndex="verified_plants"
          width={110}
          align="right"
          render={(v: number | null) =>
            v != null ? v.toLocaleString("es-ES") : "—"
          }
        />
        <Table.Column
          title="Año plant. ver."
          dataIndex="verified_crop_date"
          width={130}
          align="right"
          render={(v: number | null) => v ?? "—"}
        />
        <Table.Column
          title="Aforo (kg)"
          dataIndex="verified_kg_plot"
          width={120}
          align="right"
          render={(v: number | null) => fmtKg(v)}
        />
        <Table.Column
          title="Verificado por"
          dataIndex="verified_by_name"
          width={150}
          render={(v: string | null) => v || "—"}
        />
        <Table.Column
          title="Fecha verificación"
          dataIndex="verified_at"
          width={150}
          render={(v: string | null) => fmtDate(v)}
        />
      </Table>
    );
  };

  const renderRevisions = () => {
    if (!data) return null;
    if (data.revisions.length === 0) {
      return (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Sin revisiones registradas"
        />
      );
    }
    return (
      <Table
        dataSource={data.revisions}
        rowKey="id"
        size="small"
        pagination={false}
        expandable={{
          expandedRowRender: (r: Revision) => {
            const keys = Object.keys(r.changed_fields || {});
            if (keys.length === 0) {
              return (
                <Typography.Text type="secondary">
                  Sin diff registrado.
                </Typography.Text>
              );
            }
            return (
              <Table
                dataSource={keys.map((k) => ({
                  key: k,
                  field: k,
                  old: r.changed_fields[k]?.old,
                  newv: r.changed_fields[k]?.new,
                }))}
                rowKey="key"
                size="small"
                pagination={false}
              >
                <Table.Column title="Campo" dataIndex="field" />
                <Table.Column
                  title="Valor anterior"
                  dataIndex="old"
                  render={(v) => (
                    <code style={{ fontSize: 12 }}>
                      {v === null
                        ? "null"
                        : v === undefined
                          ? "—"
                          : typeof v === "object"
                            ? JSON.stringify(v)
                            : String(v)}
                    </code>
                  )}
                />
                <Table.Column
                  title="Valor nuevo"
                  dataIndex="newv"
                  render={(v) => (
                    <code style={{ fontSize: 12 }}>
                      {v === null
                        ? "null"
                        : v === undefined
                          ? "—"
                          : typeof v === "object"
                            ? JSON.stringify(v)
                            : String(v)}
                    </code>
                  )}
                />
              </Table>
            );
          },
        }}
      >
        <Table.Column
          title="Fecha"
          dataIndex="created"
          width={150}
          render={fmtDateTime}
        />
        <Table.Column
          title="Tipo de cambio"
          dataIndex="change_summary"
          width={180}
          render={(v: string) => <Tag color="cyan">{v}</Tag>}
        />
        <Table.Column
          title="Auditoría"
          width={180}
          render={(_: any, r: Revision) =>
            r.audit_id ? (
              <a onClick={() => onNavigateToAudit?.(r.audit_id!)}>
                #{r.audit_id}
                {r.audit_start_date && ` · ${fmtDate(r.audit_start_date)}`}
              </a>
            ) : (
              "—"
            )
          }
        />
        <Table.Column
          title="Por"
          dataIndex="changed_by_name"
          width={140}
          render={(v: string | null) => v || "—"}
        />
        <Table.Column
          title="Variedad (snapshot)"
          dataIndex="variety_name"
          width={180}
          render={(v: string | null) =>
            v ? <Tag color="blue">{v}</Tag> : "—"
          }
        />
      </Table>
    );
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={1100}
      destroyOnClose
      title={
        <Space>
          <InfoCircleOutlined />
          <span>
            Detalle parcela canónica
            {data?.name ? ` — ${data.name}` : ""}
          </span>
        </Space>
      }
    >
      {loading && (
        <div style={{ textAlign: "center", padding: 48 }}>
          <Spin size="large" />
        </div>
      )}
      {error && !loading && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={error} />
      )}
      {!loading && !error && data && (
        <Tabs
          defaultActiveKey="general"
          items={[
            {
              key: "general",
              label: "Información general",
              children: renderGeneral(),
            },
            {
              key: "snapshots",
              label: `Histórico por campaña (${data.snapshots.length})`,
              children: renderSnapshots(),
            },
            {
              key: "revisions",
              label: `Histórico de cambios (${data.revisions.length})`,
              children: renderRevisions(),
            },
          ]}
        />
      )}
    </Modal>
  );
};

export default RealPlotDetailModal;
